import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Star, ShoppingCart, Truck, Shield, ArrowLeft, Plus, Minus, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetailPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const { addToCart, loading: cartLoading } = useCart();

  useEffect(() => {
    fetchProduct();
    fetchReviews();
  }, [slug]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${slug}`);
      setProduct(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching product:', error);
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const productRes = await axios.get(`${API}/products/${slug}`);
      if (productRes.data._id) {
        const response = await axios.get(`${API}/reviews/product/${productRes.data._id}`);
        setReviews(response.data.reviews);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleAddToCart = async () => {
    const success = await addToCart(product._id, quantity);
    if (success) {
      toast({
        title: "Added to cart!",
        description: `${quantity} × ${product.name} added to your cart.`,
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="h-12 w-12 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50">
        <Sparkles className="h-16 w-16 text-stone-300 mb-4" />
        <h1 className="text-4xl font-bold mb-4 text-stone-900">Product Not Found</h1>
        <Link to="/shop/iphone-cases">
          <Button className="bg-amber-600 hover:bg-amber-700 rounded-none">Browse Products</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="product-detail-page">
      {/* Top Kilim Border */}
      <div className="h-2"
        style={{
          backgroundImage: `repeating-linear-gradient(90deg, 
            #78350f 0px, #78350f 30px, 
            #dc2626 30px, #dc2626 60px, 
            #f59e0b 60px, #f59e0b 90px)`
        }}
      ></div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Link to="/shop/iphone-cases" className="inline-flex items-center text-stone-600 hover:text-amber-600 mb-6 font-medium" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Images */}
          <div>
            <div className="bg-white overflow-hidden shadow-xl mb-4 relative">
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                className="w-full aspect-square object-cover"
                data-testid="product-main-image"
              />
              {/* Frame */}
              <div className="absolute inset-0 border-4 border-amber-600/20 pointer-events-none"></div>
            </div>
            {product.images.length > 1 && (
              <div className="grid grid-cols-4 gap-4">
                {product.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`overflow-hidden border-2 transition-all ${
                      selectedImage === index ? 'border-amber-600' : 'border-stone-200 hover:border-amber-400'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img src={image} alt={`${product.name} ${index + 1}`} className="w-full aspect-square object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            {product.badge && (
              <Badge className="mb-4 bg-amber-600 rounded-none" data-testid="product-badge">{product.badge}</Badge>
            )}
            <h1 className="text-4xl font-black text-stone-900 mb-4" data-testid="product-name">{product.name}</h1>
            
            {/* Rating */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-5 w-5 ${
                      i < Math.floor(product.rating)
                        ? 'text-amber-400 fill-amber-400'
                        : 'text-stone-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-lg text-stone-600">{product.rating} ({product.review_count} reviews)</span>
            </div>

            {/* Price */}
            <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-600">
              <div className="flex items-baseline gap-4">
                <span className="text-5xl font-black text-stone-900" data-testid="product-price">${product.price}</span>
                {product.original_price && (
                  <span className="text-2xl text-stone-500 line-through">${product.original_price}</span>
                )}
              </div>
              {product.original_price && (
                <span className="text-green-600 font-semibold">
                  Save ${(product.original_price - product.price).toFixed(2)}
                </span>
              )}
            </div>

            {/* Description */}
            <p className="text-lg text-stone-700 mb-6 leading-relaxed">{product.description}</p>

            {/* Features */}
            {product.features && product.features.length > 0 && (
              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3 text-stone-900">Key Features:</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Shield className="h-5 w-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-stone-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Compatibility */}
            <div className="bg-stone-100 p-4 mb-6">
              <h4 className="font-semibold mb-2 text-stone-900">Compatible with:</h4>
              <div className="flex flex-wrap gap-2">
                {product.device_compatibility.map((device, index) => (
                  <Badge key={index} variant="outline" className="rounded-none border-stone-400">{device}</Badge>
                ))}
              </div>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center border-2 border-stone-300">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-stone-100"
                  data-testid="decrease-qty"
                >
                  <Minus className="h-5 w-5" />
                </button>
                <span className="px-6 py-3 font-semibold text-lg" data-testid="quantity">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="p-3 hover:bg-stone-100"
                  data-testid="increase-qty"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
              <Button
                onClick={handleAddToCart}
                disabled={cartLoading || product.stock === 0}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg rounded-none"
                size="lg"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
              </Button>
            </div>

            {/* Stock Status */}
            <p className={`mb-6 font-medium ${product.stock < 10 ? 'text-orange-600' : 'text-green-600'}`} data-testid="stock-status">
              {product.stock === 0
                ? 'Out of stock'
                : product.stock < 10
                ? `Only ${product.stock} left in stock!`
                : 'In stock'}
            </p>

            {/* Shipping Info */}
            <div className="border-t border-stone-200 pt-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-12 h-12 bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Truck className="h-6 w-6 text-amber-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900">Free Shipping</h4>
                  <p className="text-sm text-stone-600">On orders over $50</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-red-100 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-red-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-stone-900">Lifetime Warranty</h4>
                  <p className="text-sm text-stone-600">All products covered</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        {reviews.length > 0 && (
          <div className="mt-16">
            <div className="flex items-center gap-3 mb-8">
              <Sparkles className="h-6 w-6 text-amber-600" />
              <h2 className="text-3xl font-black text-stone-900">Customer Reviews</h2>
            </div>
            <div className="grid gap-6">
              {reviews.map((review) => (
                <div key={review._id} className="bg-white p-6 shadow-lg border-l-4 border-amber-600">
                  <div className="flex items-center gap-2 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`h-5 w-5 ${
                          i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-stone-300'
                        }`}
                      />
                    ))}
                  </div>
                  <h4 className="font-semibold text-lg mb-2 text-stone-900">{review.title}</h4>
                  <p className="text-stone-700 mb-3">{review.comment}</p>
                  <p className="text-sm text-stone-500">
                    By {review.user_name} • {new Date(review.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
